import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Grow, List, ListItem, ListItemText, Paper, Stack, TextField, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { supplierCarService } from '@api/services/services';
import { SupplierCarTypeDto, SupplierCarTypeLanguageVariantDto } from '@api/models/suppliers/SupplierCarTypeDto';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { TableRowButton } from '../common/basic/TableRowButton';
import { getLanguageVariantName } from '../../utils/format';

interface Props {
  onCarTypeUpdated?: () => void;
}

export const CarTypeManagement: FC<Props> = ({ onCarTypeUpdated }) => {
  const { t, i18n } = useTranslation();
  const [carTypes, setCarTypes] = useState<SupplierCarTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingCarType, setAddingCarType] = useState(false);
  const [newCarTypeVariants, setNewCarTypeVariants] = useState<Record<string, string>>({});
  const [expandedCarTypeId, setExpandedCarTypeId] = useState<string | null>(null);
  const [editedVariants, setEditedVariants] = useState<Record<string, string>>({});
  const [savingCarTypeId, setSavingCarTypeId] = useState<string | null>(null);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const applicationLanguages = useMemo(() => companyCustomization?.applicationLanguages ?? [], [companyCustomization]);

  const fetchCarTypes = async () => {
    try {
      setLoading(true);
      const response = await supplierCarService.getAllCarTypesByCompany();
      setCarTypes(response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarTypes();
  }, []);

  const handleNewCarTypeChange = (language: string, value: string) => {
    setNewCarTypeVariants((prev) => ({
      ...prev,
      [language]: value,
    }));
  };

  const handleAddCarType = async () => {
    const mainLanguage = companyCustomization?.defaultLanguage;
    const mainLanguageValue = mainLanguage ? newCarTypeVariants[mainLanguage] : undefined;
    if (!mainLanguageValue?.trim()) return;

    try {
      setAddingCarType(true);

      const languageVariants: SupplierCarTypeLanguageVariantDto[] = applicationLanguages
        ?.filter((lang) => newCarTypeVariants[lang]?.trim())
        .map((lang) => ({
          language: lang,
          name: newCarTypeVariants[lang],
        }));

      const carTypeDto: SupplierCarTypeDto = {
        languageVariants,
      };

      await supplierCarService.createCarType(carTypeDto);

      setNewCarTypeVariants({});
      await fetchCarTypes();
      onCarTypeUpdated?.();
    } finally {
      setAddingCarType(false);
    }
  };

  const handleDeleteCarType = async (carTypeId: string) => {
    await supplierCarService.deleteCarType(carTypeId);
    await fetchCarTypes();
    onCarTypeUpdated?.();
  };

  const handleCarTypeClick = (carType: SupplierCarTypeDto) => {
    if (expandedCarTypeId === carType.id) {
      // Collapse if already expanded
      setExpandedCarTypeId(null);
      setEditedVariants({});
    } else {
      // Expand and populate edited variants
      setExpandedCarTypeId(carType.id!);
      const variantsMap: Record<string, string> = {};
      carType.languageVariants?.forEach((variant) => {
        if (variant.language) {
          variantsMap[variant.language] = variant.name || '';
        }
      });
      setEditedVariants(variantsMap);
    }
  };

  const handleEditVariant = (language: string, value: string) => {
    setEditedVariants((prev) => ({
      ...prev,
      [language]: value,
    }));
  };

  const handleSaveCarType = async (carTypeId: string) => {
    try {
      setSavingCarTypeId(carTypeId);

      const languageVariants: SupplierCarTypeLanguageVariantDto[] = applicationLanguages
        .filter((lang) => editedVariants[lang]?.trim())
        .map((lang) => ({
          language: lang,
          name: editedVariants[lang],
        }));

      const carTypeDto: SupplierCarTypeDto = {
        id: carTypeId,
        languageVariants,
      };

      await supplierCarService.updateCarType(carTypeId, carTypeDto);
      await fetchCarTypes();
      setExpandedCarTypeId(null);
      setEditedVariants({});
      onCarTypeUpdated?.();
    } finally {
      setSavingCarTypeId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const mainLanguage = companyCustomization?.defaultLanguage;
  const hasMainLanguageInput = mainLanguage ? Boolean(newCarTypeVariants[mainLanguage]?.trim()) : false;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('supplier.car.manageCarTypes')}
      </Typography>

      {/* Add new car type section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('supplier.car.addNewCarType')}
        </Typography>

        <Stack spacing={2}>
          {applicationLanguages.map((lang, index) => {
            const isMainLanguage = lang === mainLanguage;
            const shouldShow = isMainLanguage || hasMainLanguageInput;

            return (
              <Grow
                key={lang}
                in={shouldShow}
                timeout={{
                  enter: 300 + index * 100,
                  exit: 300 + (applicationLanguages.length - index) * 100,
                }}
                unmountOnExit
              >
                <TextField
                  fullWidth
                  size="small"
                  label={isMainLanguage && !newCarTypeVariants[lang] ? t(`supplier.car.newCarTypeName`) : t(`languages.${lang}`)}
                  value={newCarTypeVariants[lang] || ''}
                  onChange={(e) => handleNewCarTypeChange(lang, e.target.value)}
                  disabled={addingCarType}
                  required={isMainLanguage}
                  error={isMainLanguage && newCarTypeVariants[lang] !== undefined && !newCarTypeVariants[lang]?.trim()}
                />
              </Grow>
            );
          })}

          <Button variant="contained" onClick={handleAddCarType} disabled={!hasMainLanguageInput || addingCarType} sx={{ alignSelf: 'flex-start' }}>
            {addingCarType ? <CircularProgress size={24} /> : t('common.add')}
          </Button>
        </Stack>
      </Paper>

      {/* Existing car types list */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('supplier.car.existingCarTypes')}
      </Typography>

      <List>
        {carTypes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('supplier.car.noCarTypes')}
          </Typography>
        ) : (
          carTypes.map((carType) => {
            const isExpanded = expandedCarTypeId === carType.id;
            const isSaving = savingCarTypeId === carType.id;

            return (
              <Paper
                key={carType.id}
                elevation={isExpanded ? 4 : 1}
                sx={{
                  mb: 2,
                  transition: 'all 0.3s ease-in-out',
                  border: '1px solid',
                  borderColor: isExpanded ? 'primary.main' : 'divider',
                  bgcolor: isExpanded ? 'action.hover' : 'background.paper',
                }}
              >
                <ListItem
                  onClick={() => handleCarTypeClick(carType)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                  secondaryAction={
                    <TableRowButton
                      disabled={carType.isInternal}
                      onClick={() => handleDeleteCarType(carType.id!)}
                    >
                      {!carType.isInternal && <DeleteForeverIcon color={'error'} />}
                    </TableRowButton>
                  }
                >
                  <ListItemText primary={getLanguageVariantName(carType.languageVariants, i18n.language)} />
                  <Box
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease-in-out',
                      mr: 5,
                    }}
                  >
                    <ExpandMoreIcon />
                  </Box>
                </ListItem>

                <Collapse in={isExpanded} timeout={400} unmountOnExit>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Stack spacing={2}>
                      {applicationLanguages.map((lang, index) => (
                        <Grow key={lang} in={isExpanded} timeout={200 + index * 100}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t(`languages.${lang}`)}
                            value={editedVariants[lang] || ''}
                            onChange={(e) => handleEditVariant(lang, e.target.value)}
                            disabled={isSaving}
                            sx={{
                              '&:first-of-type': {
                                marginTop: '1rem',
                              },
                            }}
                          />
                        </Grow>
                      ))}

                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={() => handleSaveCarType(carType.id!)}
                        disabled={isSaving}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {isSaving ? t('common.saving') : t('common.save')}
                      </Button>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            );
          })
        )}
      </List>
    </Box>
  );
};
